import { useTranslation } from 'react-i18next'
import styles from './Addresses.module.scss'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { formatContractStatus, isInActiveAddress } from 'utils/contracts.ts'
import { ContractStatus } from './types.ts'
import dayjs from 'dayjs'
import { Button, Icon, Tag } from '@boltenergy-be/design-system'
import { useState } from 'react'
import { setMoveFlow } from 'store/app/slice'
import { routes } from 'types/routes'
import { ContractFlowStartPoints, ContractFlowTrackingTypes } from 'types/tracking'
import { Link, useNavigate } from 'react-router'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import { capitalize } from 'utils/format.ts'
import { IS_BETA } from 'constants/envs.ts'
import { FaqSlugB2C } from 'types/faq.ts'
import ShowInactiveAddresses from 'pages/app/addresses/components/ShowInactiveAddresses.tsx'
import { selectCurrentAccount, selectIsProducerAccount } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { MOVE_FLOWS, BillingContractWithServiceContracts } from '@boltenergy-be/shared'
import AddressEanTooltip from 'components/address-ean-tooltip/AddressEanTooltip.tsx'
import BlockResubscriptionModal from 'components/block-resubscription-modal/BlockResubscriptionModal.tsx'
import { useCheckResubscriptionStatus } from 'hooks/useCheckResubscriptionStatus.ts'

const Addresses = () => {
  // Redux
  const { billingContracts } = useStoreSelector((store) => store.contact)
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const currentAccount = useStoreSelector(selectCurrentAccount)
  const dispatch = useStoreDispatch()

  // Hooks
  const resubscriptionStatus = useCheckResubscriptionStatus()

  // Local constants
  const { billingContracts: accountBillingContracts } = currentAccount
  const inactiveContracts = accountBillingContracts.filter((contract) => isInActiveAddress(billingContracts[contract]))
  const inactiveContractsCount = inactiveContracts.length

  // React router
  const navigate = useNavigate()

  // i18n
  const { t } = useTranslation('addresses')

  // Local state
  const [showInactive, setShowInactive] = useState(inactiveContractsCount === accountBillingContracts.length)
  const [showResubscriptionBlockModal, setShowResubscriptionBlockModal] = useState<boolean>(false)

  /**
   * Get the contract status
   * @param {BillingContract} contract
   * @returns {ContractStatus[]}
   */
  const getContractStatus = ({ serviceContracts }: BillingContractWithServiceContracts): ContractStatus[] => {
    const electricityStatus = formatContractStatus(serviceContracts.electricity)
    const gasStatus = serviceContracts?.gas ? formatContractStatus(serviceContracts.gas) : null

    if (!gasStatus) {
      return [
        {
          type: electricityStatus.type,
          contractType: 'EL',
          text: t(($) => $.overview.status[electricityStatus.key], {
            date: formatStatusDate(electricityStatus.date)
          })
        } satisfies ContractStatus
      ]
    }

    if (JSON.stringify(electricityStatus) === JSON.stringify(gasStatus)) {
      return [
        {
          type: electricityStatus.type,
          contractType: 'EL+GAS',
          text: t(($) => $.overview.status[electricityStatus.key], {
            date: formatStatusDate(electricityStatus.date)
          })
        } satisfies ContractStatus
      ]
    }

    return [
      {
        type: electricityStatus.type,
        icon: 'electricity',
        contractType: 'EL',
        text: t(($) => $.overview.status[electricityStatus.key], {
          date: formatStatusDate(electricityStatus.date)
        })
      },
      {
        type: gasStatus.type,
        icon: 'gas',
        contractType: 'GAS',
        text: t(($) => $.overview.status[gasStatus.key], {
          date: formatStatusDate(gasStatus.date)
        })
      }
    ] satisfies ContractStatus[]
  }

  const formatStatusDate = (date: string | undefined) => {
    return dayjs(date).format('D MMMM YYYY')
  }

  /**
   * Check resubscription status before starting add contract flow
   */
  const handleAddNewAddress = () => {
    const flow = MOVE_FLOWS.start
    trackEvent(
      {
        category: 'contract',
        object: 'add_contract',
        action: 'start'
      },
      { flow, type: ContractFlowTrackingTypes.ADD, from: ContractFlowStartPoints.ADDRESSES }
    )
    dispatch(setMoveFlow(flow))
    void navigate(routes.CONTRACTS_ADD)
  }

  return (
    <>
      <PageLayout title={t(($) => $.overview.title)} classes={{ body: styles.body }} faqCategories={{ b2c: [FaqSlugB2C.MY_ADDRESSES] }}>
        <ul className={styles['address-list']}>
          {accountBillingContracts
            .filter((contract) => (showInactive ? true : !isInActiveAddress(billingContracts[contract])))
            .map((c) => {
              const contract = billingContracts[c]
              const statuses = getContractStatus(contract)

              return (
                <li key={contract.contractNumber}>
                  <Link
                    to={routes.CONTRACT_DETAIL.replace(':id', contract.contractNumber)}
                    onMouseDown={() => trackEvent({ category: 'addresses', object: 'address_detail', action: 'view' })}
                    className={styles.address}
                  >
                    <div className={styles['address-first-line']}>
                      <strong>
                        {capitalize(contract.serviceContracts.electricity.deliveryPoint.address.streetName)}{' '}
                        {contract.serviceContracts.electricity.deliveryPoint.address.streetNumber}
                        {contract.serviceContracts.electricity.deliveryPoint.address.streetBox
                          ? `, ${contract.serviceContracts.electricity.deliveryPoint.address.streetBox}`
                          : ''}
                        <AddressEanTooltip serviceContracts={contract.serviceContracts} placement="right-end" />
                      </strong>
                      <Icon name="chevronRight" className={styles.icon} />
                    </div>
                    <span>
                      {contract.serviceContracts.electricity.deliveryPoint.address.postalCode}{' '}
                      {capitalize(contract.serviceContracts.electricity.deliveryPoint.address.townName)}
                    </span>
                    <div className={styles.statuses}>
                      {statuses.map((status) => (
                        <Tag key={`${contract.contractNumber}-${status.contractType}`} size="small" {...status} icon={status.icon}>
                          {status.text}
                        </Tag>
                      ))}
                    </div>
                  </Link>
                </li>
              )
            })}
        </ul>

        {!IS_BETA && !isProducer && (
          <Button
            isFullwidthMobile
            leadingIcon="homePlus"
            variant="secondary"
            className={styles['action-button']}
            onClick={() => {
              if (resubscriptionStatus === 'block') {
                setShowResubscriptionBlockModal(true)
              } else {
                handleAddNewAddress()
              }
            }}
            loading={resubscriptionStatus === 'checking'}
          >
            {t(($) => $.myContract.addContract)}
          </Button>
        )}

        <ShowInactiveAddresses {...{ showInactive, setShowInactive, inactiveContractsCount }} />
      </PageLayout>

      <BlockResubscriptionModal
        isOpen={showResubscriptionBlockModal}
        onRequestClose={() => setShowResubscriptionBlockModal(false)}
        onContinue={handleAddNewAddress}
      />
    </>
  )
}

export default Addresses
